import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { identityApi } from '@/api/identity.api';
import { PortalDataList } from './PortalDataList';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { notify } from '@/core/toast/notify';
import { showApiError } from '@/utils/apiError';

export function CustomerPortalSections() {
  const { pathname } = useLocation();
  const section = pathname.split('/').pop() ?? 'dashboard';

  if (section === 'tickets') return <CustomerTicketsSection />;
  if (section === 'invoices') return <CustomerListSection title="Invoices" loader={identityApi.customerInvoices} primaryKey="invoiceNumber" metaKey="statusCode" />;
  if (section === 'orders') return <CustomerListSection title="Orders" loader={identityApi.customerOrders} primaryKey="orderNumber" metaKey="status" />;
  if (section === 'payments') return <CustomerListSection title="Payments" loader={identityApi.customerPayments} primaryKey="invoiceNumber" metaKey="amount" />;
  if (section === 'profile') return <CustomerProfileSection />;
  if (section === 'documents' || section === 'subscriptions' || section === 'notifications') {
    return <PortalDataList title={section.charAt(0).toUpperCase() + section.slice(1)} items={[]} emptyMessage="No items yet." />;
  }
  return <CustomerListSection title="Invoices" loader={identityApi.customerInvoices} primaryKey="invoiceNumber" metaKey="statusCode" />;
}

function CustomerProfileSection() {
  const [profile, setProfile] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void identityApi.customerProfile()
      .then(setProfile)
      .catch((e) => showApiError(e, 'Could not load profile'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PortalDataList
      title="Profile"
      loading={loading}
      items={profile?.name ? [{
        id: String(profile.id ?? 1),
        primary: String(profile.name),
        secondary: profile.email ? String(profile.email) : undefined,
      }] : []}
    />
  );
}

function CustomerListSection({
  title,
  loader,
  primaryKey,
  metaKey,
}: {
  title: string;
  loader: () => Promise<Array<Record<string, unknown>>>;
  primaryKey: string;
  metaKey: string;
}) {
  const [items, setItems] = useState<Array<{ id: string | number; primary: string; secondary?: string; meta?: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void loader()
      .then((rows) => {
        if (cancelled) return;
        setItems(rows.map((r, i) => ({
          id: Number(r.id ?? i),
          primary: String(r[primaryKey] ?? title),
          meta: r[metaKey] != null ? String(r[metaKey]) : undefined,
        })));
      })
      .catch((e) => showApiError(e, `Could not load ${title.toLowerCase()}`))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [title, primaryKey, metaKey, loader]);

  return <PortalDataList title={title} loading={loading} items={items} emptyMessage={`No ${title.toLowerCase()} yet.`} />;
}

function CustomerTicketsSection() {
  const [tickets, setTickets] = useState<Array<{ id: number; subject: string; statusName?: string }>>([]);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await identityApi.customerTickets();
      setTickets(data);
    } catch (err) {
      showApiError(err, 'Could not load tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const submit = async () => {
    try {
      await identityApi.createCustomerTicket({ subject, description });
      notify.success('Ticket created');
      setSubject('');
      setDescription('');
      void load();
    } catch (err) {
      showApiError(err, 'Failed to create ticket');
    }
  };

  return (
    <div className="space-y-6">
      <PortalDataList
        title="Support tickets"
        loading={loading}
        items={tickets.map((t) => ({
          id: t.id,
          primary: t.subject,
          meta: t.statusName ?? 'Open',
        }))}
        emptyMessage="No support tickets."
      />
      <section className="card p-4 space-y-3">
        <h2 className="font-semibold">New ticket</h2>
        <Input label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <Textarea label="Description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        <Button onClick={submit}>Submit</Button>
      </section>
    </div>
  );
}
